import { useCallback, useEffect, useRef, useState } from "react";
import type { Position } from "./geo";
import { haversine } from "./snap";
import { apiFetch, apiUrl } from "./api";

export type ManeuverType =
  | "turn" | "new name" | "depart" | "arrive" | "merge" | "ramp" | "on ramp"
  | "off ramp" | "fork" | "end of road" | "continue" | "roundabout"
  | "rotary" | "roundabout turn" | "notification" | "exit roundabout"
  | "exit rotary" | "uturn";

export type ManeuverModifier =
  | "uturn" | "sharp right" | "right" | "slight right" | "straight"
  | "slight left" | "left" | "sharp left";

export type Step = {
  name: string;
  ref?: string;
  distance: number;   // metres in this step
  duration: number;   // seconds in this step
  maneuver: {
    type: ManeuverType;
    modifier?: ManeuverModifier;
    location: [number, number]; // [lon, lat]
    bearingBefore?: number;
    bearingAfter?: number;
  };
  geometry: { type: "LineString"; coordinates: [number, number][] };
};

export type Route = {
  distance: number;
  duration: number;
  geometry: { type: "LineString"; coordinates: [number, number][] };
  steps: Step[];
};

export type GeocodeResult = {
  lat: number;
  lon: number;
  name: string;
  line: string;
  type?: string;
  category?: string;
  importance?: number;
};

export async function geocode(q: string, near?: Position | null, signal?: AbortSignal): Promise<GeocodeResult[]> {
  const params = new URLSearchParams({ q });
  if (near) {
    params.set("lat", String(near.lat));
    params.set("lon", String(near.lon));
  }
  const r = await apiFetch(`/api/geocode?${params}`, { signal });
  if (!r.ok) return [];
  const data = await r.json();
  return data.results || [];
}

export async function fetchRoute(from: { lat: number; lon: number }, to: { lat: number; lon: number }): Promise<Route | null> {
  const params = new URLSearchParams({
    fromLat: String(from.lat),
    fromLon: String(from.lon),
    toLat: String(to.lat),
    toLon: String(to.lon),
  });
  const r = await apiFetch(`/api/route?${params}`);
  if (!r.ok) return null;
  return r.json();
}

/* =============================================================
   Progress tracker — given the current GPS position, figure out
   how far along the route we are and what the next maneuver is.
   ============================================================= */

export type NavProgress = {
  /** Step the driver is currently traversing. */
  currentStepIndex: number;
  /** Metres remaining in the current step (to the next maneuver). */
  distanceToNextManeuver: number;
  /** Seconds remaining for the entire route. */
  timeRemaining: number;
  /** Metres remaining for the entire route. */
  distanceRemaining: number;
  /** Perpendicular distance (m) from the position to the route polyline.
   *  Large + sustained ⇒ the driver has left the route ⇒ reroute. */
  offRouteMeters: number;
  /** True when driver is within ~30m of the destination. */
  arrived: boolean;
};

/**
 * Walk the polyline forward from the current position, finding the
 * nearest segment. From there, sum residual distances to each step
 * boundary, then to the destination. Pure JS, runs every position update.
 */
export function computeProgress(route: Route, pos: Position): NavProgress {
  const coords = route.geometry.coordinates;
  const stepBoundaries = cumulativeStepBoundaries(route);

  // Find nearest segment + cumulative distance from route start to that point.
  let best = { segIdx: 0, cumStartToPoint: 0, snapLat: coords[0][1], snapLon: coords[0][0], distToRoute: Infinity };
  let cum = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const [aLon, aLat] = coords[i];
    const [bLon, bLat] = coords[i + 1];
    const segLen = haversine(aLat, aLon, bLat, bLon);
    const { t, snapLat, snapLon, dist } = pointToSegment(pos.lat, pos.lon, aLat, aLon, bLat, bLon);
    if (dist < best.distToRoute) {
      best = {
        segIdx: i,
        cumStartToPoint: cum + segLen * t,
        snapLat,
        snapLon,
        distToRoute: dist,
      };
    }
    cum += segLen;
  }
  const totalLen = cum;

  // Distance remaining = total - cumStartToPoint
  const distanceRemaining = Math.max(0, totalLen - best.cumStartToPoint);

  // Find current step: smallest index whose cumulative-end is greater than our cum position.
  let stepIdx = stepBoundaries.findIndex((b) => b.cumEnd > best.cumStartToPoint);
  if (stepIdx < 0) stepIdx = stepBoundaries.length - 1;
  const currentStep = stepBoundaries[stepIdx];
  const distanceToNextManeuver = Math.max(0, currentStep.cumEnd - best.cumStartToPoint);

  // Linear ETA: scale by progress fraction.
  const fractionDone = totalLen > 0 ? best.cumStartToPoint / totalLen : 0;
  const timeRemaining = Math.max(0, route.duration * (1 - fractionDone));

  // Arrival when within 30 m of destination AND we're past the last meaningful step
  const last = coords[coords.length - 1];
  const distToDest = haversine(pos.lat, pos.lon, last[1], last[0]);
  const arrived = distToDest < 30 || distanceRemaining < 20;

  return {
    currentStepIndex: stepIdx,
    distanceToNextManeuver,
    timeRemaining,
    distanceRemaining,
    offRouteMeters: best.distToRoute,
    arrived,
  };
}

function cumulativeStepBoundaries(route: Route) {
  // For each step, compute the cumulative distance along the *full route*
  // at which it ends. Steps share endpoints with the next step's start.
  const out: { cumStart: number; cumEnd: number }[] = [];
  let cum = 0;
  for (const s of route.steps) {
    const len = polylineLength(s.geometry.coordinates);
    out.push({ cumStart: cum, cumEnd: cum + len });
    cum += len;
  }
  return out;
}

function polylineLength(coords: [number, number][]) {
  let total = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const [aLon, aLat] = coords[i];
    const [bLon, bLat] = coords[i + 1];
    total += haversine(aLat, aLon, bLat, bLon);
  }
  return total;
}

/**
 * Project query point onto the segment a→b using a local flat-earth
 * approximation. Returns t (0..1), the snapped lat/lon, and distance in m.
 */
function pointToSegment(qLat: number, qLon: number, aLat: number, aLon: number, bLat: number, bLon: number) {
  const mLat = 111_320;
  const mLon = 111_320 * Math.cos((qLat * Math.PI) / 180);
  const ax = aLon * mLon, ay = aLat * mLat;
  const bx = bLon * mLon, by = bLat * mLat;
  const qx = qLon * mLon, qy = qLat * mLat;
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 === 0 ? 0 : ((qx - ax) * dx + (qy - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const px = ax + t * dx;
  const py = ay + t * dy;
  const ddx = qx - px, ddy = qy - py;
  return {
    t,
    snapLat: py / mLat,
    snapLon: px / mLon,
    dist: Math.sqrt(ddx * ddx + ddy * ddy),
  };
}

/* =============================================================
   useNavigation — orchestrates the whole flow.
   ============================================================= */

export type NavState = {
  destination: GeocodeResult | null;
  route: Route | null;
  progress: NavProgress | null;
  loading: boolean;
  /** True while a reroute (off-route recalculation) is in flight. */
  rerouting: boolean;
  error: string | null;
};

// Off-route thresholds. The position fed to nav is already snapped to a
// road, so once the driver is on a *different* road the perpendicular
// distance to the route polyline jumps well past this. We require it to
// stay over the threshold for a sustained window so a momentary GPS
// excursion (or the route polyline running slightly off the real lane)
// doesn't trigger a needless recalculation.
const OFF_ROUTE_METERS = 45;
const OFF_ROUTE_SUSTAIN_MS = 4000;
const REROUTE_COOLDOWN_MS = 10_000;

export function useNavigation(position: Position | null) {
  const [state, setState] = useState<NavState>({
    destination: null,
    route: null,
    progress: null,
    loading: false,
    rerouting: false,
    error: null,
  });
  const posRef = useRef(position);
  posRef.current = position;

  // Off-route / reroute bookkeeping.
  const reroutingRef = useRef(false);
  const offRouteSinceRef = useRef<number | null>(null);
  const cooldownUntilRef = useRef(0);

  const startNavigation = useCallback(async (dest: GeocodeResult) => {
    const from = posRef.current;
    if (!from) { setState((s) => ({ ...s, error: "Ingen GPS-position endnu" })); return; }
    offRouteSinceRef.current = null;
    cooldownUntilRef.current = 0;
    setState({ destination: dest, route: null, progress: null, loading: true, rerouting: false, error: null });
    try {
      const route = await fetchRoute({ lat: from.lat, lon: from.lon }, { lat: dest.lat, lon: dest.lon });
      if (!route) {
        setState({ destination: dest, route: null, progress: null, loading: false, rerouting: false, error: "Kunne ikke finde en rute" });
        return;
      }
      setState({ destination: dest, route, progress: null, loading: false, rerouting: false, error: null });
    } catch (e) {
      setState({ destination: dest, route: null, progress: null, loading: false, rerouting: false, error: String(e) });
    }
  }, []);

  const cancelNavigation = useCallback(() => {
    offRouteSinceRef.current = null;
    setState({ destination: null, route: null, progress: null, loading: false, rerouting: false, error: null });
  }, []);

  // Recalculate from the current position to the (unchanged) destination
  // when the driver has left the route. Keeps the old route on screen
  // until the new one arrives so navigation never blanks out.
  const reroute = useCallback(async (dest: GeocodeResult) => {
    const from = posRef.current;
    if (!from || reroutingRef.current) return;
    reroutingRef.current = true;
    offRouteSinceRef.current = null;
    setState((s) => (s.destination === dest ? { ...s, rerouting: true } : s));
    try {
      const route = await fetchRoute({ lat: from.lat, lon: from.lon }, { lat: dest.lat, lon: dest.lon });
      setState((s) => {
        if (s.destination !== dest) return s; // nav was cancelled / changed mid-fetch
        if (!route) return { ...s, rerouting: false };
        return { ...s, route, progress: null, rerouting: false, error: null };
      });
    } catch {
      setState((s) => ({ ...s, rerouting: false }));
    } finally {
      reroutingRef.current = false;
      cooldownUntilRef.current = Date.now() + REROUTE_COOLDOWN_MS;
    }
  }, []);

  // Update progress whenever position changes, while a route is active.
  useEffect(() => {
    if (!state.route || !position) return;
    const progress = computeProgress(state.route, position);
    setState((s) => (s.route ? { ...s, progress } : s));

    if (progress.arrived) {
      offRouteSinceRef.current = null;
      const id = window.setTimeout(() => cancelNavigation(), 5000);
      return () => clearTimeout(id);
    }

    // Off-route detection → reroute (debounced + cooldown-gated).
    const dest = state.destination;
    if (dest && !reroutingRef.current && Date.now() > cooldownUntilRef.current) {
      if (progress.offRouteMeters > OFF_ROUTE_METERS) {
        const now = Date.now();
        if (offRouteSinceRef.current == null) offRouteSinceRef.current = now;
        else if (now - offRouteSinceRef.current > OFF_ROUTE_SUSTAIN_MS) reroute(dest);
      } else {
        offRouteSinceRef.current = null;
      }
    }
  }, [state.route, state.destination, position?.lat, position?.lon, cancelNavigation, reroute]);

  return { state, startNavigation, cancelNavigation };
}

/* =============================================================
   Step formatting — turn OSRM maneuver into human-readable text.
   ============================================================= */

export function maneuverInstruction(step: Step): string {
  const m = step.maneuver;
  const mod = m.modifier || "straight";
  const onto = step.name ? ` ad ${step.name}` : "";
  if (m.type === "depart") return "Start kørsel" + onto;
  if (m.type === "arrive") return "Du er fremme";
  if (m.type === "roundabout" || m.type === "rotary") return `Tag rundkørslen${onto}`;
  if (m.type === "merge") return `Flet ${trMod(mod)}${onto}`;
  if (m.type === "on ramp") return `Kør på${onto}`;
  if (m.type === "off ramp") return `Kør af${onto}`;
  if (m.type === "fork") return `Hold ${trMod(mod)}${onto}`;
  if (m.type === "end of road") return `Drej ${trMod(mod)}${onto}`;
  if (m.type === "continue" || m.type === "new name") {
    if (mod === "straight") return `Fortsæt ligeud${onto}`;
    return `Fortsæt ${trMod(mod)}${onto}`;
  }
  if (m.type === "turn") {
    if (mod === "uturn") return `Foretag U-vending${onto}`;
    return `Drej ${trMod(mod)}${onto}`;
  }
  return `${trMod(mod)}${onto}`;
}

function trMod(m: ManeuverModifier | string): string {
  switch (m) {
    case "left": return "til venstre";
    case "right": return "til højre";
    case "slight left": return "skråt til venstre";
    case "slight right": return "skråt til højre";
    case "sharp left": return "skarpt til venstre";
    case "sharp right": return "skarpt til højre";
    case "straight": return "ligeud";
    case "uturn": return "U-vending";
    default: return m;
  }
}

export function formatDistance(m: number): string {
  if (m < 50) return `${Math.round(m / 10) * 10} m`;
  if (m < 1000) return `${Math.round(m / 50) * 50} m`;
  return `${(m / 1000).toFixed(m < 10_000 ? 1 : 0)} km`;
}

export function formatDuration(s: number): string {
  if (s < 60) return `${Math.round(s)} sek`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return `${h} t ${m} min`;
}

export function arrivalTime(s: number): string {
  const d = new Date(Date.now() + s * 1000);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}
